import React, { useEffect, useRef, useState, useCallback, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import RemoveIcon from "@material-ui/icons/Remove";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { getSocket } from "../../services/socket-io";
import { useFloatingChat } from "../../context/FloatingChat";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import useTurnosChatAccess from "../../hooks/useTurnosChatAccess";
import { isTurnosProfile } from "../../constants/turnosProfiles";
import MessagesList from "../MessagesList";
import MessageInput from "../MessageInput";
// Tamaño relativo al viewport + la invariante "el popup nunca tapa su propia X".
// Vive aparte para que popupSize.test.mjs lo pueda evaluar sin React ni MUI.
import {
  clamp,
  effectiveSize,
  limitsFor,
  VIEWPORT_GAP,
} from "./popupSize";

// Cuenta regresiva de la sesión de turnos (feat_273). Timer 1s SOLO mientras el
// chat esté expandido (este componente se monta/desmonta con el popup). onExpire
// vía ref para no re-crear el interval en cada render.
const calcRemaining = (expiresAt) =>
  Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
const fmtMMSS = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const TurnosCountdown = ({ expiresAt, sessionId, onExpire, onExtend }) => {
  const [remaining, setRemaining] = useState(() => calcRemaining(expiresAt));
  const [extending, setExtending] = useState(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    setRemaining(calcRemaining(expiresAt));
    const timer = setInterval(() => {
      const r = calcRemaining(expiresAt);
      setRemaining(r);
      if (r <= 0) {
        clearInterval(timer);
        onExpireRef.current();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  const low = remaining <= 120; // <2 min

  const handleExtend = async () => {
    setExtending(true);
    try {
      const { data } = await api.put(`/turnos-chat/sessions/${sessionId}/extend`);
      onExtend(data.expiresAt);
    } catch (e) {
      toast.error(i18n.t("turnosChat.sessionError"));
    }
    setExtending(false);
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        padding: "2px 6px",
        borderRadius: 10,
        whiteSpace: "nowrap",
        background: low ? "#c62828" : "rgba(255,255,255,0.2)",
        color: "#fff",
      }}
    >
      {`⏸ ${i18n.t("turnosChat.iaPaused")} · ${fmtMMSS(remaining)}`}
      {low && (
        <Button
          size="small"
          onClick={handleExtend}
          disabled={extending}
          style={{ color: "#fff", fontSize: 10, minWidth: 0, padding: "0 4px" }}
        >
          {i18n.t("turnosChat.keepAttending")}
        </Button>
      )}
    </span>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    position: "fixed",
    bottom: 0,
    right: 0,
    // 🔴 Estaba en 1200 y el AppBar de la app está en 1201 (medido en el
    // navegador): el AppBar quedaba ENCIMA del popup. En escritorio no se nota
    // (el popup vive abajo a la derecha), pero en móvil el popup es fullscreen
    // desde top:0 → su header, con la X de cerrar, quedaba TAPADO por la barra
    // superior y el click ni llegaba (elementFromPoint devolvía el AppBar).
    // 1250: por encima del AppBar (1201) y del Drawer permanente (1200), y
    // sigue por DEBAJO de los modales MUI (1300) como antes.
    zIndex: 1250,
    display: "flex",
    alignItems: "flex-end",
    pointerEvents: "none",
  },
  bubbleColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 10,
    pointerEvents: "auto",
  },
  bubbleColumnMobile: {
    position: "fixed",
    top: 8,
    right: 8,
    left: "auto",
    flexDirection: "row",
    zIndex: 1201,
  },
  bubble: {
    cursor: "pointer",
    boxShadow: theme.shadows[6],
    "&:hover $bubbleClose": { display: "flex" },
  },
  bubbleAvatar: {
    width: 48,
    height: 48,
  },
  bubbleClose: {
    display: "none",
    position: "absolute",
    top: -6,
    right: -6,
    width: 20,
    height: 20,
    background: theme.palette.grey[700],
    color: "#fff",
    borderRadius: "50%",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    zIndex: 1,
  },
  popup: {
    pointerEvents: "auto",
    position: "fixed",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    boxShadow: theme.shadows[8],
  },
  popupMobile: {
    position: "fixed",
    inset: 0,
    width: "100%",
    // `height: 100%` de un elemento `fixed` se resuelve contra el bloque
    // contenedor inicial, que en iOS Safari es el viewport GRANDE (el de la barra
    // de direcciones oculta). Con la barra visible, el popup queda más alto que lo
    // que se ve y el input de mensaje cae por debajo del borde. `100dvh` sigue al
    // viewport visible; `100vh` queda como fallback para navegadores sin dvh.
    height: "100vh",
    "@supports (height: 100dvh)": { height: "100dvh" },
    maxHeight: "100vh",
    borderRadius: 0,
  },
  resizeHandle: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 18,
    height: 18,
    cursor: "nwse-resize",
    // 🔴 ACÁ MURIÓ EL RESIZE. El handle vive en la esquina superior-izquierda, o
    // sea DEBAJO del header. Mientras el header fue `position: static` su
    // `z-index` era inerte y este handle (absoluto, z 2) pintaba encima. El fix
    // de la X (087f02cf) le puso al header `position:relative` + `zIndex:3` y
    // desde ahí el header se comió el `mousedown`: medido con
    // `elementFromPoint` en la esquina, el que gana es el header (`cursor:grab`,
    // z 3) y el gesto se convertía en un DRAG DE POSICIÓN, no en un resize.
    // z 4 lo devuelve arriba sin tocar el header, que sigue por encima del
    // cuerpo (z 3) como pide el fix de anoche.
    zIndex: 4,
    // Triángulo en la esquina superior-izquierda. Opaco: sobre el verde del
    // header (#075E54) es la única pista visual de que el popup se redimensiona.
    background:
      "linear-gradient(135deg, #fff 0%, #fff 45%, transparent 46%)",
    opacity: 0.85,
    "&:hover": { opacity: 1 },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px",
    background: "#075E54",
    color: "#fff",
    cursor: "grab",
    userSelect: "none",
    // El header NUNCA se comprime ni se va de pantalla: es el único lugar desde
    // donde se cierra el chat.
    flexShrink: 0,
    position: "relative",
    zIndex: 3,
  },
  // En móvil los botones del header tienen que ser tocables de verdad (≥44×44,
  // mínimo recomendado por las guías de iOS/Android). El `size="small"` de MUI
  // deja 30 px y en pantalla chica se falla el toque.
  headerBtnMobile: {
    minWidth: 44,
    minHeight: 44,
    padding: 10,
  },
  headerNameBlock: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    lineHeight: 1.15,
  },
  headerName: {
    display: "block",
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  // feat_282: línea de salida del ticket ("vía <línea>"), texto chico y atenuado.
  headerVia: {
    display: "block",
    fontSize: 11,
    fontWeight: 400,
    opacity: 0.75,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  headerBtn: {
    color: "#fff",
    padding: 4,
  },
  body: {
    flex: 1,
    minHeight: 0,
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    background: "#e5ddd5",
  },
  // Aviso dentro del cuerpo (cargando / error). Reemplaza al popup vacío.
  bodyNotice: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: 20,
    textAlign: "center",
    color: "#4a4a4a",
    fontSize: 13,
    lineHeight: 1.4,
  },
  bodyNoticeIcon: {
    fontSize: 34,
    color: "#b26a00",
  },
}));

// Deriva nombre visible del contacto (patrón feat_032/033).
const contactLabel = (contact, fallback) =>
  contact?.name || contact?.whatsappName || fallback;

const FloatingChat = () => {
  const classes = useStyles();
  const isMobile = useMediaQuery("(max-width:600px)");

  // ── Tamaño del viewport, vivo ───────────────────────────────────────────────
  // 🔴 EL BUG DE MÓVIL VIVÍA ACÁ. `isMobile` mira SOLO el ancho (max-width:600px),
  // así que un teléfono ACOSTADO (667×375, 844×390) NO es "mobile": cae en el
  // popup de escritorio, 400×620 px fijos, anclado a `bottom:0`. Con 375 px de
  // alto el popup arranca en y = 375 − 620 = −245 → el HEADER, y con él la X de
  // cerrar, queda ARRIBA DEL VIEWPORT. No está tapada por nada: está fuera.
  // El usuario no puede cerrar el chat ni haciendo scroll (el popup es `fixed`).
  // Lo mismo pasa en cualquier ventana de escritorio más baja que el popup.
  // Por eso el alto SIEMPRE se acota al viewport, y por eso las proporciones de
  // `defaultSizeFor` son un TECHO, no un alto garantizado.
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1024,
    h: typeof window !== "undefined" ? window.innerHeight : 768,
  }));
  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    // orientationchange: en iOS el resize llega antes de que innerHeight se
    // actualice; el segundo pase lo corrige.
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  const {
    chats,
    position,
    size,
    setPosition,
    setSize,
    persistSize,
    closeChat,
    minimizeChat,
    expandChat,
    setChatExpiry,
  } = useFloatingChat();
  const { user } = useContext(AuthContext);
  const turnosChat = useTurnosChatAccess(user);

  // Tamaño efectivo: la preferencia guardada se acota contra el viewport actual
  // (un alto elegido en el monitor 3K no puede desbordar la notebook de después);
  // sin preferencia, sale de la proporción del viewport.
  const { w: popupW, h: popupH } = effectiveSize(size, viewport.w, viewport.h);

  // Cache de datos del ticket (contacto/estado/ticket completo) por ticketId.
  const [tickets, setTickets] = useState({});
  // Estado de carga por ticketId: "loading" | {error: <status>}. Antes el
  // `.catch(() => {})` de más abajo se comía el 404 de GET /tickets/:id y el
  // popup quedaba ABIERTO Y VACÍO — sin mensajes, sin campo para escribir y sin
  // una sola pista de por qué. Eso es lo que el asesor reportaba como "el chat
  // no se abre".
  const [loadState, setLoadState] = useState({});
  const [unread, setUnread] = useState({});
  // Diálogo de cierre del modo turnos (feat_273): { ticketId, sessionId }.
  const [closeDialog, setCloseDialog] = useState(null);
  const dragRef = useRef(null);

  const expanded = chats.find((c) => !c.minimized) || null;
  const minimizedChats = chats.filter((c) => c.minimized);

  // Carga (o recarga) los datos de UN ticket. Extraída del effect para que el
  // botón "Reintentar" pueda volver a pedir: antes el effect solo corría al
  // cambiar `chats` y el guard `if (tickets[id]) return;` hacía que, tras un
  // fallo, NUNCA se volviera a pedir en toda la sesión.
  const fetchTicket = useCallback((ticketId) => {
    setLoadState((prev) => ({ ...prev, [ticketId]: "loading" }));
    api
      .get(`/tickets/${ticketId}`)
      .then(({ data }) => {
        setTickets((prev) => ({ ...prev, [ticketId]: data }));
        setLoadState((prev) => ({ ...prev, [ticketId]: "ok" }));
      })
      .catch((err) => {
        const status = err?.response?.status || 0;
        console.warn(`[FloatingChat] GET /tickets/${ticketId} falló (HTTP ${status})`);
        setLoadState((prev) => ({ ...prev, [ticketId]: { error: status } }));
      });
  }, []);

  // Fetch de datos para cada chat abierto que aún no esté en cache.
  useEffect(() => {
    chats.forEach((c) => {
      if (tickets[c.ticketId]) return;
      // Sin esto un fallo permanente (404) reintentaría en cada render del
      // effect. El reintento manual limpia el estado y vuelve a entrar.
      if (loadState[c.ticketId]) return;
      fetchTicket(c.ticketId);
    });
    // Limpiar cache/unread/estado de chats cerrados.
    const openIds = new Set(chats.map((c) => c.ticketId));
    setTickets((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        if (openIds.has(Number(k))) next[k] = prev[k];
      });
      return next;
    });
    setLoadState((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        if (openIds.has(Number(k))) next[k] = prev[k];
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats]);

  // Reset unread del chat expandido (se marca leído al abrir MessagesList).
  useEffect(() => {
    if (expanded) {
      setUnread((prev) => ({ ...prev, [expanded.ticketId]: 0 }));
    }
  }, [expanded]);

  // Badge de no leídos para chats minimizados: escucha appMessage vía notification room.
  useEffect(() => {
    if (!chats.length) return undefined;
    const socket = getSocket();
    const join = () => socket.emit("joinNotification");
    join();
    socket.on("connect", join);
    const handler = (data) => {
      if (data.action !== "create") return;
      const tid = Number(data.message?.ticketId ?? data.ticket?.id);
      // Los salientes no cuentan como no leídos. La extensión de la sesión de
      // turnos ya NO se refetchea acá: el backend emite `turnosChatSession`
      // {action:"extend", expiresAt} que actualiza el countdown en vivo (abajo).
      if (data.message?.fromMe) return;
      setUnread((prev) => {
        // Solo cuenta si el chat está abierto Y minimizado (no el expandido).
        const chat = chats.find((c) => c.ticketId === tid);
        if (!chat || !chat.minimized) return prev;
        return { ...prev, [tid]: (prev[tid] || 0) + 1 };
      });
    };
    socket.on("appMessage", handler);
    return () => {
      socket.off("connect", join);
      socket.off("appMessage", handler);
    };
  }, [chats]);

  // feat_274/279: sesión de turnos extendida/abierta en el backend → refrescar
  // el countdown del header con el `expiresAt` del propio payload del socket
  // (reemplaza el GET /sessions/active redundante que Fase B disparaba por
  // mensaje). setChatExpiry es no-op si el ticketId no está entre los chats.
  useEffect(() => {
    const socket = getSocket();
    const onSession = (payload) => {
      if (!payload || !payload.ticketId) return;
      if (
        (payload.action === "extend" || payload.action === "open") &&
        payload.expiresAt
      ) {
        setChatExpiry(payload.ticketId, payload.expiresAt);
      }
    };
    socket.on("turnosChatSession", onSession);
    return () => socket.off("turnosChatSession", onSession);
  }, [setChatExpiry]);

  // ── Drag del popup expandido ────────────────────────────────────────────────
  const onDragStart = useCallback(
    (e) => {
      if (isMobile) return;
      const startX = e.clientX;
      const startY = e.clientY;
      const rect = dragRef.current?.getBoundingClientRect();
      const baseX = rect ? rect.left : window.innerWidth - popupW - 70;
      const baseY = rect ? rect.top : window.innerHeight - popupH;
      const move = (ev) => {
        let x = baseX + (ev.clientX - startX);
        let y = baseY + (ev.clientY - startY);
        // Clamp al viewport.
        x = Math.max(0, Math.min(x, window.innerWidth - popupW));
        y = Math.max(0, Math.min(y, window.innerHeight - popupH));
        setPosition({ x, y });
      };
      const up = () => {
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    },
    [isMobile, setPosition, popupW, popupH]
  );

  // ── Resize desde la esquina superior-izquierda (bottom-right fijo) ───────────
  const onResizeStart = useCallback(
    (e) => {
      if (isMobile) return;
      e.stopPropagation();
      e.preventDefault();
      const rect = dragRef.current?.getBoundingClientRect();
      if (!rect) return;
      const fixedRight = rect.right;
      const fixedBottom = rect.bottom;
      let last = null;
      const move = (ev) => {
        // Los límites se releen de `window` en cada movimiento, no de la captura
        // del render: si la ventana cambia de tamaño a mitad del arrastre, el
        // techo acompaña.
        const L = limitsFor(window.innerWidth, window.innerHeight);
        const w = clamp(fixedRight - ev.clientX, L.minW, L.maxW);
        const h = clamp(fixedBottom - ev.clientY, L.minH, L.maxH);
        last = { w, h };
        setSize(last);
        setPosition({
          x: Math.max(0, fixedRight - w),
          y: Math.max(0, fixedBottom - h),
        });
      };
      const up = () => {
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
        // Persistir UNA vez al soltar, no en cada mousemove (serían cientos de
        // escrituras a localStorage por arrastre).
        if (last) persistSize(last);
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    },
    [isMobile, setSize, setPosition, persistSize]
  );

  // ── Cierre de la sesión de turnos (feat_273) ────────────────────────────────
  const closeTurnosSession = useCallback(
    async (chat, keepHuman) => {
      try {
        if (chat.sessionId) {
          await api.delete(`/turnos-chat/sessions/${chat.sessionId}`, {
            data: { keepHuman },
          });
        }
      } catch (e) {
        toast.error(i18n.t("turnosChat.sessionError"));
      }
      closeChat(chat.ticketId);
    },
    [closeChat]
  );

  // ✕ en un chat de turnos: sin diálogo para profesional/agendador sin acceso a
  // Chats (default seguro = devolver IA); diálogo de 3 salidas para el resto.
  const handleCloseClick = useCallback(
    (chat) => {
      if (chat.origin !== "turnos") {
        closeChat(chat.ticketId);
        return;
      }
      const restricted = isTurnosProfile(user?.profile) && !turnosChat.enabled;
      if (restricted) {
        closeTurnosSession(chat, false);
        return;
      }
      setCloseDialog({ ticketId: chat.ticketId, sessionId: chat.sessionId });
    },
    [closeChat, closeTurnosSession, user, turnosChat.enabled]
  );

  // Timeout de la cuenta regresiva → cerrar + avisar que la IA retomó.
  const handleTurnosExpire = useCallback(
    (ticketId) => {
      closeChat(ticketId);
      toast.info(i18n.t("turnosChat.iaResumed"));
    },
    [closeChat]
  );

  if (!chats.length) return null;

  // El alto/ancho pedidos son un TECHO: nunca pueden pasarse del viewport, o el
  // header (y la X de cerrar) se van de pantalla. Ver el comentario de `viewport`.
  const effH = Math.max(200, Math.min(popupH, viewport.h - VIEWPORT_GAP));
  const effW = Math.min(popupW, viewport.w - VIEWPORT_GAP);
  const popupStyle = isMobile
    ? undefined
    : position
    ? {
        width: effW,
        height: effH,
        // El top también se acota: una ventana achicada después de arrastrar
        // dejaba el popup (y la X) fuera igual.
        left: clamp(position.x, 0, Math.max(0, viewport.w - effW)),
        top: clamp(position.y, 0, Math.max(0, viewport.h - effH)),
        right: "auto",
        bottom: "auto",
      }
    : { width: effW, height: effH, right: 70, bottom: 0 };

  const expandedData = expanded ? tickets[expanded.ticketId] : null;
  const expandedState = expanded ? loadState[expanded.ticketId] : null;
  const expandedContact = expandedData?.contact;

  return (
    <div className={classes.root}>
      {/* Burbujas minimizadas */}
      {minimizedChats.length > 0 && (
        <div
          className={`${classes.bubbleColumn} ${
            isMobile ? classes.bubbleColumnMobile : ""
          }`}
        >
          {minimizedChats.map((c) => {
            const data = tickets[c.ticketId];
            const contact = data?.contact;
            const label = contactLabel(contact, c.contactName);
            return (
              <Tooltip key={c.ticketId} title={label} placement="left">
                <div
                  className={classes.bubble}
                  style={{ position: "relative" }}
                  onClick={() => expandChat(c.ticketId)}
                >
                  <Badge
                    color="secondary"
                    badgeContent={unread[c.ticketId] || 0}
                    overlap="circular"
                  >
                    <Avatar
                      className={classes.bubbleAvatar}
                      src={contact?.profilePicUrl || undefined}
                    >
                      {label?.[0]?.toUpperCase()}
                    </Avatar>
                  </Badge>
                  <span
                    className={classes.bubbleClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseClick(c);
                    }}
                  >
                    ✕
                  </span>
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}

      {/* Popup expandido */}
      {expanded && (
        <Paper
          ref={dragRef}
          className={`${classes.popup} ${isMobile ? classes.popupMobile : ""}`}
          style={popupStyle}
          elevation={8}
        >
          {!isMobile && (
            <div
              className={classes.resizeHandle}
              onMouseDown={onResizeStart}
              title={i18n.t("floatingChat.resize")}
            />
          )}
          <div className={classes.header} onMouseDown={onDragStart}>
            <Avatar
              style={{ width: 30, height: 30 }}
              src={expandedContact?.profilePicUrl || undefined}
            >
              {contactLabel(expandedContact, expanded.contactName)?.[0]?.toUpperCase()}
            </Avatar>
            <div className={classes.headerNameBlock}>
              <span className={classes.headerName}>
                {contactLabel(expandedContact, expanded.contactName)}
              </span>
              {(() => {
                // Línea de salida: prioridad al dato del by-phone (feat_281/282);
                // fallback al whatsapp del GET /tickets/:id (ShowTicketService).
                const viaName =
                  expanded.whatsappName || expandedData?.whatsapp?.name;
                return viaName ? (
                  <span className={classes.headerVia}>
                    {`${i18n.t("floatingChat.via")} ${viaName}`}
                  </span>
                ) : null;
              })()}
            </div>
            {expanded.origin === "turnos" && expanded.expiresAt && (
              <TurnosCountdown
                expiresAt={expanded.expiresAt}
                sessionId={expanded.sessionId}
                onExpire={() => handleTurnosExpire(expanded.ticketId)}
                onExtend={(exp) => setChatExpiry(expanded.ticketId, exp)}
              />
            )}
            <Tooltip title={i18n.t("floatingChat.minimize")}>
              <IconButton
                size="small"
                className={`${classes.headerBtn} ${isMobile ? classes.headerBtnMobile : ""}`}
                onClick={() => minimizeChat(expanded.ticketId)}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={i18n.t("floatingChat.close")}>
              <IconButton
                size="small"
                className={`${classes.headerBtn} ${isMobile ? classes.headerBtnMobile : ""}`}
                onClick={() => handleCloseClick(expanded)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
          <div className={classes.body}>
            {/* El popup NUNCA queda mudo: o hay conversación, o hay un mensaje
                que dice qué pasó con un botón para reintentar. */}
            {!expandedData && expandedState && expandedState.error ? (
              <div className={classes.bodyNotice}>
                <ErrorOutlineIcon className={classes.bodyNoticeIcon} />
                <span>
                  {expandedState.error === 404 || expandedState.error === 403
                    ? i18n.t("floatingChat.loadErrorDenied")
                    : i18n.t("floatingChat.loadError")}
                </span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => fetchTicket(expanded.ticketId)}
                >
                  {i18n.t("floatingChat.retry")}
                </Button>
              </div>
            ) : null}
            {!expandedData && (!expandedState || expandedState === "loading") ? (
              <div className={classes.bodyNotice}>
                <CircularProgress size={22} />
                <span>{i18n.t("floatingChat.loading")}</span>
              </div>
            ) : null}
            {expandedData ? (
              // ReplyMessageProvider por ticketId: MessageInput consume
              // ReplyMessageContext (setReplyingMessage). Sin este provider el
              // context queda undefined → crash → pantalla en blanco (feat_270).
              // Estado de mensaje citado NO se comparte entre chats (key por id).
              <ReplyMessageProvider key={expanded.ticketId}>
                <MessagesList
                  ticketId={expanded.ticketId}
                  isGroup={expandedData.isGroup}
                  compact
                />
                <MessageInput
                  ticketId={expanded.ticketId}
                  // feat_273: en modo turnos, status "open" efectivo (bypass de
                  // escritura sobre tickets pending). Otros chats = status real.
                  ticketStatus={
                    expanded.origin === "turnos" ? "open" : expandedData.status
                  }
                  contact={expandedContact}
                  ticket={expandedData}
                  compact
                  turnosSessionId={
                    expanded.origin === "turnos" ? expanded.sessionId : null
                  }
                />
              </ReplyMessageProvider>
            ) : null}
          </div>
        </Paper>
      )}

      {/* Diálogo de cierre del modo turnos (feat_273) */}
      <Dialog
        open={!!closeDialog}
        onClose={() => setCloseDialog(null)}
        style={{ zIndex: 1400 }}
      >
        <DialogTitle>{i18n.t("turnosChat.closeDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("turnosChat.closeDialogBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialog(null)} color="default">
            {i18n.t("turnosChat.continueAttending")}
          </Button>
          <Button
            onClick={() => {
              const d = closeDialog;
              setCloseDialog(null);
              if (d) closeTurnosSession({ ticketId: d.ticketId, sessionId: d.sessionId }, true);
            }}
            color="default"
          >
            {i18n.t("turnosChat.keepHuman")}
          </Button>
          <Button
            onClick={() => {
              const d = closeDialog;
              setCloseDialog(null);
              if (d) closeTurnosSession({ ticketId: d.ticketId, sessionId: d.sessionId }, false);
            }}
            color="primary"
            variant="contained"
            autoFocus
          >
            {i18n.t("turnosChat.returnToIa")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FloatingChat;
